import { useKV } from '@/hooks/useLocalStorage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  BookOpen,
  GraduationCap,
  Sparkle,
} from '@phosphor-icons/react';
import { Tutorial } from '@/lib/tutorialTypes';

interface WelcomeScreenProps {
  tutorials: Tutorial[];
  onTutorialStart: (tutorial: Tutorial) => void;
  onSkip: () => void;
}

export function WelcomeScreen({
  tutorials,
  onTutorialStart,
  onSkip,
}: Readonly<WelcomeScreenProps>) {
  const [hasSeenWelcome, setHasSeenWelcome] = useKV<boolean>(
    'has-seen-welcome',
    false
  );

  // Show welcome screen on first visit
  const shouldShow = !hasSeenWelcome;

  const handleGetStarted = () => {
    setHasSeenWelcome(true);
    onTutorialStart(tutorials[0]); // Start first tutorial
  };

  const handleSkip = () => {
    setHasSeenWelcome(true);
    onSkip();
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <Dialog open={shouldShow} onOpenChange={() => {}}>
      <DialogContent className='max-w-4xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <div className='text-center'>
            <div className='w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center'>
              <Sparkle className='w-8 h-8 text-primary' />
            </div>
            <DialogTitle className='text-2xl'>
              Welcome to DataLab Explorer!
            </DialogTitle>
            <DialogDescription className='mt-3 text-base'>
              Your journey into data science starts here. We'll guide you
              through everything you need to know.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className='space-y-6'>
          {/* What You'll Learn */}
          <Card className='border-blue-200 bg-blue-50/30'>
            <CardHeader>
              <CardTitle className='flex items-center gap-2 text-blue-800'>
                <BookOpen className='w-5 h-5' />
                What You'll Learn
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-3'>
                  <div className='flex items-start gap-3'>
                    <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                      1
                    </div>
                    <div>
                      <h4 className='font-medium text-blue-800'>
                        Find Quality Data
                      </h4>
                      <p className='text-sm text-blue-700'>
                        Discover and evaluate datasets from real organizations
                      </p>
                    </div>
                  </div>
                  <div className='flex items-start gap-3'>
                    <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                      2
                    </div>
                    <div>
                      <h4 className='font-medium text-blue-800'>
                        Analyze Patterns
                      </h4>
                      <p className='text-sm text-blue-700'>
                        Find meaningful connections between different datasets
                      </p>
                    </div>
                  </div>
                </div>
                <div className='space-y-3'>
                  <div className='flex items-start gap-3'>
                    <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                      3
                    </div>
                    <div>
                      <h4 className='font-medium text-blue-800'>
                        Create Visualizations
                      </h4>
                      <p className='text-sm text-blue-700'>
                        Turn numbers into compelling charts and stories
                      </p>
                    </div>
                  </div>
                  <div className='flex items-start gap-3'>
                    <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                      4
                    </div>
                    <div>
                      <h4 className='font-medium text-blue-800'>
                        Share Insights
                      </h4>
                      <p className='text-sm text-blue-700'>
                        Communicate your discoveries effectively
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tutorial Preview */}
          <div>
            <h3 className='text-lg font-semibold mb-4 text-center'>
              Interactive Learning Path
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {tutorials.slice(0, 4).map((tutorial, index) => {
                return (
                  <Card key={tutorial.id} className='relative overflow-hidden'>
                    <CardHeader className='pb-3'>
                      <div className='flex items-start gap-3'>
                        <div className='p-2 rounded-lg bg-primary/10 text-primary flex-shrink-0'>
                          {tutorial.icon}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <CardTitle className='text-base line-clamp-1'>
                            {tutorial.title}
                          </CardTitle>
                          <div className='flex items-center gap-2 mt-1'>
                            <Badge
                              variant='outline'
                              className={`text-xs ${getDifficultyColor(tutorial.difficulty)}`}
                            >
                              {tutorial.difficulty}
                            </Badge>
                            <span className='text-xs text-muted-foreground'>
                              {tutorial.estimatedTime}
                            </span>
                          </div>
                        </div>
                      </div>
                      <CardDescription className='text-sm line-clamp-2'>
                        {tutorial.description}
                      </CardDescription>
                    </CardHeader>

                    {/* Progress indicator for recommended flow */}
                    {index < 3 && (
                      <div className='absolute -right-2 top-1/2 transform -translate-y-1/2 z-10'>
                        <div className='w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center'>
                          <ArrowRight className='w-3 h-3 text-primary' />
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex items-center justify-between pt-6 border-t'>
            <div className='text-sm text-muted-foreground'>
              💡 Tutorials are interactive and take just 5-8 minutes each
            </div>
            <div className='flex gap-3'>
              <Button variant='ghost' onClick={handleSkip}>
                I'll explore on my own
              </Button>
              <Button onClick={handleGetStarted} className='gap-2'>
                <GraduationCap className='w-4 h-4' />
                Start Learning Journey
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
